import React, { useState, useMemo, useEffect, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, Table, Form, Input, Button } from 'antd';
import { useGetReservationForecast } from 'hooks/useGetReservationForecast';
import { generateExportFileName, getEarliestValidDate, toLocalTime, toUtcTime } from 'utils';
import { getColumnReservationForecast } from 'constants/table';
import { getRangeDate, setRangeDate, clearRangeDate } from 'utils/dateRangeStorage';
import 'styles/reservation-forecast.scss';
import { FormProps } from 'rc-field-form';
import set from 'lodash/set';

import ExportReservationForecast from 'components/common/export-reservation-forecast';
import { useTranslation } from 'react-i18next';

const INITIAL_RANGE_DATE = 14;
const initStartDate = dayjs().startOf('day');

function ReservationForecast() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isFirstRender = useRef(true);

  const [filterOptions, setFilterOptions] = useState<any>(() => {
    const savedRange = getRangeDate();
    if (savedRange) {
      const startDate = dayjs(savedRange.startDate);
      return {
        start_date: toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD'),
        period: savedRange.period
      };
    }
    return {
      start_date: toUtcTime(initStartDate.add(1, 'day'), 'YYYY-MM-DD'),
      period: INITIAL_RANGE_DATE
    };
  });
  const { data: reservationForecast, isFetching } = useGetReservationForecast(filterOptions);

  const dataSource = useMemo(() => {
    if (!reservationForecast) return [];
    const dataArray = reservationForecast.map(item => ({
      ...item,
      date: toLocalTime(item.date, 'DD/MM/YYYY ddd')
    }));

    const totalRevenue = dataArray.reduce((sum, item) => sum + (item.financials?.revenue || 0), 0);
    const totalGrossProfit = dataArray.reduce(
      (sum, item) => sum + (item.financials?.room_revenue || 0),
      0
    );
    const totalRoomAvailable = dataArray.reduce(
      (sum, item) => sum + (item.occupied?.total_room_available || 0),
      0
    );
    const totalRoomsCheckIn = dataArray.reduce((sum, item) => sum + (item.check_in?.room || 0), 0);
    const totalGuestsCheckIn = dataArray.reduce((sum, item) => sum + (item.check_in?.pax || 0), 0);
    const totalRoomsCheckOut = dataArray.reduce(
      (sum, item) => sum + (item.check_out?.room || 0),
      0
    );
    const totalGuestsCheckOut = dataArray.reduce(
      (sum, item) => sum + (item.check_out?.pax || 0),
      0
    );
    const totalRoomsOcc = dataArray.reduce((sum, item) => sum + (item.occupied?.room || 0), 0);
    const totalGuestsOcc = dataArray.reduce((sum, item) => sum + (item.occupied?.pax || 0), 0);

    const averageOccupiedPercent = (totalRoomsOcc / totalRoomAvailable) * 100;

    const averageGrossProfit = dataArray.length > 0 ? totalGrossProfit / totalRoomsOcc : 0;
    const averageNetProfit = dataArray.length > 0 ? totalRevenue / totalRoomsOcc : 0;
    const totalRoomsLocked = dataArray.reduce((sum, item) => sum + (item.rooms_locked || 0), 0);

    const averageRow = {
      date: t('managePage.reservationForecastPage.total'),
      check_in: { room: totalRoomsCheckIn, pax: totalGuestsCheckIn },
      check_out: { room: totalRoomsCheckOut, pax: totalGuestsCheckOut },
      rooms_locked: totalRoomsLocked,
      occupied: {
        room: totalRoomsOcc,
        occupied_percent: averageOccupiedPercent.toFixed(2),
        pax: totalGuestsOcc
      },
      financials: {
        revenue: totalRevenue.toFixed(0),
        gross_profit: averageGrossProfit.toFixed(0),
        net_profit: averageNetProfit.toFixed(0)
      }
    };

    return [...dataArray, averageRow];
  }, [reservationForecast, t]);

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const { rangeDate, period, ...restFilterOptions } = truthyValues;
    if (rangeDate) {
      const startDate: Dayjs = rangeDate[0];
      const endDate: Dayjs = rangeDate[1];

      if (startDate && endDate) {
        const dayCount = endDate.diff(startDate, 'day');
        set(restFilterOptions, 'period', dayCount);
        set(restFilterOptions, 'start_date', toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD'));
        setRangeDate(startDate, endDate, dayCount + 1);
      }
    }

    set(restFilterOptions, 'period', period || INITIAL_RANGE_DATE);

    setFilterOptions(restFilterOptions);
  };

  const handleRangeDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const dayCount = end.diff(start, 'day');
      form.setFieldsValue({ period: dayCount + 1 });
      setRangeDate(start, end, dayCount + 1);
    }
  };

  const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const period = parseInt(e.target.value);
    if (!isNaN(period)) {
      const startDate = dayjs(form.getFieldValue('rangeDate')[0]);
      const endDate = startDate.add(period, 'day');
      form.setFieldsValue({ rangeDate: [startDate, endDate] });
    }
  };

  // Single useEffect for initialization
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      const savedRange = getRangeDate();
      if (savedRange) {
        const startDate = dayjs(savedRange.startDate);
        const endDate = dayjs(savedRange.endDate);
        form.setFieldsValue({
          rangeDate: [startDate, endDate],
          period: savedRange.period
        });
      } else {
        const endDate = initStartDate.add(INITIAL_RANGE_DATE - 1, 'day');
        form.setFieldsValue({
          rangeDate: [initStartDate, endDate],
          period: INITIAL_RANGE_DATE
        });
      }
    }
  }, [form]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      clearRangeDate();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const fromDate = getEarliestValidDate(filterOptions.start_date);

  const toDate = filterOptions.start_date
    ? dayjs(filterOptions.start_date)
        .add(filterOptions.period, 'day')
        .subtract(1, 'day')
        .format('DD-MM-YYYY')
    : undefined;

  const columnReservationForecast = useMemo(() => {
    return getColumnReservationForecast(t);
  }, [t]);

  return (
    <div className="pms-reservation-forecast">
      <div className="pms-reservation-forecast__timetable">
        <Form form={form} onFinish={onFinish} autoComplete="off">
          <div className="pms-reservation-forecast__filter">
            <div className="pms-reservation-forecast__filter__left">
              <Form.Item name="rangeDate">
                <DatePicker.RangePicker
                  className="w-full"
                  placeholder={[t('common.time.fromDate'), t('common.time.toDate')]}
                  format="DD-MM-YYYY"
                  onChange={handleRangeDateChange}
                />
              </Form.Item>
              <Form.Item name="period">
                <Input placeholder={t('common.time.totalDays')} onChange={handlePeriodChange} />
              </Form.Item>
              <Form.Item>
                <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                  {t('common.actions.submitButton')}
                </Button>
              </Form.Item>
            </div>
            <ExportReservationForecast
              fileName={generateExportFileName('reservation_forecast')}
              enableView={!isFetching}
              dataToExport={dataSource}
              t={t}
              fromDate={fromDate}
              toDate={toDate}
            />
          </div>
        </Form>
        <div className="pms-reservation-forecast__table">
          <Table
            rowKey="date"
            loading={isFetching}
            columns={columnReservationForecast}
            rowClassName={record =>
              record.date === t('managePage.reservationForecastPage.total') ? 'bold-row' : ''
            }
            dataSource={dataSource}
            pagination={false}
            scroll={{ x: 1000, y: 'calc(100vh - 320px)' }}
            bordered
            locale={{
              emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default ReservationForecast;
